<template>
  <div>
    <v-card-title v-if="!hideCardTitle">
      <span>{{ report.title }}</span>
      <v-spacer></v-spacer>
      <report-status-chip :status="report.status" small> </report-status-chip>
    </v-card-title>
    <v-card-subtitle v-if="!hideCardTitle">
      <duty-type-chip :dutyType="report.type" small> </duty-type-chip>
    </v-card-subtitle>
    <slot name="header"> </slot>
    <v-divider></v-divider>

    <v-simple-table dense>
      <template v-slot:default>
        <tbody>
          <tr>
            <td class="font-weight-medium">Bezeichnung</td>
            <td>
              <v-chip
                v-if="!report.title"
                label
                small
                color="error"
                class="my-1"
                ><v-icon small left>mdi-alert-octagon</v-icon>Nicht
                festgelegt</v-chip
              >
              <span v-else>
                {{ report.title }}
              </span>
            </td>
          </tr>
          <tr v-if="!hideAssignedInfo">
            <td class="font-weight-medium">Berichtsverantwortlich</td>
            <td>
              <v-chip outlined small class="my-1"
                ><v-icon small left>mdi-account-circle</v-icon
                >{{ report.assignedTo?.displayName }}</v-chip
              >
            </td>
          </tr>
          <tr v-if="!hideAssignedInfo && report.correctedBy?.displayName">
            <td class="font-weight-medium">Korrekturverantwortlich</td>
            <td>
              <v-chip outlined small class="my-1"
                ><v-icon small left>mdi-account-circle</v-icon
                >{{ report.correctedBy?.displayName }}</v-chip
              >
            </td>
          </tr>
          <tr>
            <td class="font-weight-medium">Startzeitpunkt</td>
            <td>
              <v-chip
                v-if="!report.duration.start.timestamp"
                label
                small
                color="error"
                class="my-1"
                ><v-icon small left>mdi-alert-octagon</v-icon>Nicht
                festgelegt</v-chip
              >
              <span v-else>
                {{ timestampConverter(report.duration.start.timestamp) }}
                Uhr
              </span>
            </td>
          </tr>
          <tr>
            <td class="font-weight-medium">Endzeitpunkt</td>
            <td>
              <v-chip
                v-if="!report.duration.end.timestamp"
                label
                small
                color="error"
                class="my-1"
                ><v-icon small left>mdi-alert-octagon</v-icon>Nicht
                festgelegt</v-chip
              >
              <span v-else>
                {{ timestampConverter(report.duration.end.timestamp) }}
                Uhr
              </span>
              <br
                v-if="
                  !report.duration.end.timestamp &&
                  report.duration.end.estimatedTimestamp
                "
              />
              <small
                v-if="
                  !report.duration.end.timestamp &&
                  report.duration.end.estimatedTimestamp
                "
                class="grey--text mt-n0"
                >Erwartet:
                {{ timestampConverter(report.duration.end.estimatedTimestamp) }}
                Uhr
              </small>
            </td>
          </tr>
          <tr>
            <td class="font-weight-medium">Ort</td>
            <td>
              <v-chip
                v-if="!report.location.name"
                label
                small
                color="error"
                class="my-1"
                ><v-icon small left>mdi-alert-octagon</v-icon>Nicht
                festgelegt</v-chip
              >
              <span v-else>
                {{ report.location.name }}
              </span>
            </td>
          </tr>

          <tr>
            <td class="font-weight-medium">Primäre Abrechnungsart</td>
            <td>
              <v-chip
                v-if="!report.payrollType.id"
                label
                small
                color="error"
                class="my-1"
                ><v-icon small left>mdi-alert-octagon</v-icon>Nicht
                festgelegt</v-chip
              >
              <span v-else>
                {{ report.payrollType.title }}
              </span>
            </td>
          </tr>
          <tr v-if="report.incident.code || report.incident.number">
            <td class="font-weight-medium">Anlass</td>
            <td>
              <span v-if="report.incident.code">{{
                report.incident.code
              }}</span>
              <span v-if="report.incident.code && report.incident.number"
                >, </span
              ><span v-if="report.incident.number"
                >E.-Nr.:{{ report.incident.number }}</span
              >
            </td>
          </tr>
          <tr v-if="report.safetyPrecautionsDocumentation">
            <td class="font-weight-medium">UVV-Dokumentation</td>
            <td>
              {{ report.safetyPrecautionsDocumentation }}
            </td>
          </tr>
          <tr v-if="report.training?.subject">
            <td class="font-weight-medium">Ausbildungsthema</td>
            <td>
              {{ report.training.subject }}
            </td>
          </tr>
          <tr v-if="report.training?.categories?.length > 0">
            <td class="font-weight-medium">Ausbildungskategorien</td>
            <td>
              <v-chip
                v-for="(category, index) in report.training.categories"
                :key="index"
                small
                outlined
                class="mr-1"
              >
                {{ category }}
              </v-chip>
            </td>
          </tr>
          <tr v-if="report.training?.responsibleInstructors?.length > 0">
            <td class="font-weight-medium">Verantwortliche Ausbilder</td>
            <td>
              <v-chip
                v-for="(instructor, index) in report.training
                  .responsibleInstructors"
                :key="index"
                small
                outlined
                class="mr-1"
              >
                {{ instructor?.displayName }}
              </v-chip>
            </td>
          </tr>
          <tr v-if="report.notes">
            <td class="font-weight-medium">Notizen</td>
            <td>
              {{ report.notes }}
            </td>
          </tr>
          <tr v-if="report.eventId">
            <td class="font-weight-medium">Verknüpfte Veranstaltung</td>
            <td>
              <v-btn
                small
                depressed
                class="my-1"
                :to="{
                  name: 'scheduling-event-details-tab-general',
                  params: { itemId: report.eventId },
                }"
                ><v-icon small class="mr-1">mdi-link-variant</v-icon
                >Öffnen</v-btn
              >
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>
  </div>
</template>

<script>
import DutyTypeChip from "@/components/duty-management/DutyTypeChip.vue";
import ReportStatusChip from "@/components/duty-management/ReportStatusChip.vue";
export default {
  name: "report-details-card-content",
  props: {
    report: {
      type: Object,
    },
    hideCardTitle: {
      type: Boolean,
      default: false,
      required: false,
    },
    hideAssignedInfo: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  components: { DutyTypeChip, ReportStatusChip },
  methods: {
    timestampConverter(timestamp) {
      const options = {
        weekday: "long",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      };
      return timestamp.toDate().toLocaleDateString("de-DE", options);
    },
  },
  computed: {
    attendeeItems() {
      return [];
    },
  },
};
</script>
